import {
  AddToHomeScreenResult,
  FullscreenRequestOptions,
  FullscreenState,
  FullscreenStateListener,
  PointerInputListener,
  PointerLockResult,
  SystemApi,
} from './SystemApi'
import { DeviceInfo, DeviceApi } from '../device'
import { EnvironmentInfo, EnvironmentApi } from '../environment'
import { SafeArea } from '../Host'
import { RundotGameAPI } from '../rundot-game-api/types'

/**
 * Mock implementation of SystemApi for local development.
 * Delegates to mock device and environment APIs.
 */
export class MockSystemApi implements SystemApi {
  private readonly deviceApi: DeviceApi
  private readonly environmentApi: EnvironmentApi
  private readonly rundotGameAPI: RundotGameAPI

  constructor(deviceApi: DeviceApi, environmentApi: EnvironmentApi, rundotGameApi: RundotGameAPI) {
    this.deviceApi = deviceApi
    this.environmentApi = environmentApi
    this.rundotGameAPI = rundotGameApi
  }

  getDevice(): DeviceInfo {
    return this.deviceApi.getDevice()
  }

  getEnvironment(): EnvironmentInfo {
    return this.environmentApi.getEnvironment()
  }

  getSafeArea(): SafeArea {
    // Return mock safe area from stored data or fallback to defaults
    const safeArea = this.rundotGameAPI._safeAreaData

    if (!safeArea) {
      // Fallback to default mock values
      // Note: top is 0 because toolbar handles device notch positioning
      return {
        top: 0,
        right: 0,
        bottom: 34,
        left: 0
      }
    }

    // Return a copy to prevent mutation
    return { ...safeArea }
  }

  isMobile(): boolean {
    const environment = this.environmentApi.getEnvironment()

    // Check if platform is mobile
    if (environment.platform === 'ios' || environment.platform === 'android') {
      return true
    }

    // On web, check browserInfo
    if (environment.browserInfo) {
      return environment.browserInfo.isMobile
    }

    return true
  }

  isWeb(): boolean {
    const environment = this.environmentApi.getEnvironment()

    // Web platform
    if (environment.platform === 'web') {
      return true
    }

    // Check if browser indicates desktop web
    if (environment.browserInfo && !environment.browserInfo.isMobile) {
      return true
    }

    return false
  }

  isSteamDesktop(): boolean {
    return this.environmentApi.getEnvironment().isSteamDesktop
  }

  isSteamDeck(): boolean {
    return this.environmentApi.getEnvironment().isSteamDeck
  }

  async canAddToHomeScreen(): Promise<boolean> {
    return false
  }

  async addToHomeScreen(): Promise<AddToHomeScreenResult> {
    return { added: false }
  }

  async requestFullscreen(_options: FullscreenRequestOptions = {}): Promise<FullscreenState> {
    return { active: false, pointerLocked: false }
  }

  async exitFullscreen(): Promise<FullscreenState> {
    return { active: false, pointerLocked: false }
  }

  async setPointerLock(_locked: boolean): Promise<PointerLockResult> {
    return { active: false, pointerLocked: false, reason: 'unsupported' }
  }

  async getFullscreenState(): Promise<FullscreenState> {
    return { active: false, pointerLocked: false }
  }

  onFullscreenStateChange(_listener: FullscreenStateListener): () => void {
    return () => {}
  }

  onPointerInput(_listener: PointerInputListener): () => void {
    return () => {}
  }
}
